import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, Play, X, Phone, PhoneOff, Clock, CheckCircle2, XCircle, Eye,
  User, FileText, Wrench, Building2, BookOpen, ChevronDown, ChevronRight, Sparkles,
  PhoneCall, FlaskConical, FileAudio, Send, Mail, MessageSquare, Users, Moon, Trash2, UserRoundSearch,
  ListChecks, Archive, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { aiAgentsApi } from "../../lib/api";
import { formatApiErrorDetail, useAuth } from "../../contexts/AuthContext";

const err = (e, fb) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || fb);

const OUTCOME_OPTIONS = [
  { key: "estimate_accepted", label: "Estimate Accepted" },
  { key: "appointment_scheduled", label: "Appointment Scheduled" },
  { key: "requested_callback", label: "Requested Callback" },
  { key: "transferred", label: "Transferred to Office" },
  { key: "not_interested", label: "Not Interested" },
  { key: "do_not_call", label: "Do Not Call" },
  { key: "wrong_number", label: "Wrong Number" },
  { key: "voicemail_left", label: "Voicemail Left" },
  { key: "no_answer", label: "No Answer" },
  { key: "voicemail", label: "Voicemail" },
  { key: "busy_or_failed", label: "Busy / Failed" },
  { key: "customer_reached", label: "Customer Reached" },
  { key: "estimate_sent", label: "Estimate Sent" },
];

const STATUS = {
  queued: { label: "Queued", cls: "bg-slate-100 text-slate-600 border-slate-200", Icon: Clock },
  due: { label: "Due now", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Phone },
  completed: { label: "Completed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  canceled: { label: "Canceled", cls: "bg-slate-100 text-slate-400 border-slate-200", Icon: XCircle },
  skipped: { label: "Skipped", cls: "bg-slate-100 text-slate-400 border-slate-200", Icon: PhoneOff },
  failed: { label: "Failed", cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: XCircle },
};
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

const TABS = [
  { key: "followups", label: "Follow-ups", queueKind: "followup", agentType: "estimate_followup", Icon: Phone },
  { key: "memberships", label: "Memberships", queueKind: "membership", agentType: "membership_sales", Icon: Users },
];
const ENROLLMENT_TABS = [
  { key: "active", label: "Active", Icon: ListChecks },
  { key: "inactive", label: "Inactive", Icon: Archive },
];

const ENR_STATUS = {
  waiting: { label: "Waiting", cls: "bg-slate-100 text-slate-600 border-slate-200", Icon: Clock },
  calling: { label: "Calling", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: PhoneCall },
  attempts_completed: { label: "Attempts Completed", cls: "bg-slate-100 text-slate-500 border-slate-200", Icon: CheckCircle2 },
  removed_by_staff: { label: "Removed by Staff", cls: "bg-rose-50 text-rose-600 border-rose-200", Icon: Trash2 },
};
const REASON_LABEL = {
  attempts_exhausted: "All Attempts Completed",
  estimate_not_eligible: "Estimate No Longer Eligible",
  estimate_accepted: "Estimate Accepted",
  appointment_scheduled: "Appointment Scheduled",
  requested_callback: "Requested Callback",
  transferred: "Transferred to Office",
  not_interested: "Not Interested",
  do_not_call: "Do Not Call",
  wrong_number: "Wrong Number",
  estimate_sent: "Estimate Sent",
};

function CtxRow({ icon: Icon, label, children }) {
  return (
    <div className="flex gap-3">
      <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-slate-500" /></div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
        <div className="text-[13.5px] text-slate-800">{children}</div>
      </div>
    </div>
  );
}

export default function CallQueue({ campaigns, isAdmin }) {
  const { hasModule } = useAuth();
  const allTabs = [
    ...(hasModule("outbound_call_queue") ? TABS : []),
    ...(hasModule("call_queue_lifecycle") ? ENROLLMENT_TABS : []),
  ];
  const [activeTab, setActiveTab] = useState(() => allTabs[0]?.key || "followups");
  const isEnrollmentTab = activeTab === "active" || activeTab === "inactive";
  const tabDef = TABS.find((t) => t.key === activeTab);
  const tabCampaigns = tabDef ? (campaigns || []).filter(
    (c) => c.status === "active" && (c.agentType || "estimate_followup") === tabDef.agentType
  ) : [];

  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [running, setRunning] = useState(false);
  const [selCampaign, setSelCampaign] = useState("");
  const [ctxJob, setCtxJob] = useState(null);
  const [simJob, setSimJob] = useState(null);
  const [resultJob, setResultJob] = useState(null);
  const [sendJob, setSendJob] = useState(null);
  const [engine, setEngine] = useState(null);

  useEffect(() => {
    if (isEnrollmentTab) return;
    setSelCampaign(tabCampaigns[0]?.id || "");
    setFilter("all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const load = async () => {
    setLoading(true);
    try {
      const base = { queueKind: tabDef.queueKind };
      const [s, j, e] = await Promise.all([
        aiAgentsApi.callJobStats(base),
        aiAgentsApi.listCallJobs(filter === "all" ? base : { ...base, status: filter }),
        engine ? Promise.resolve(engine) : aiAgentsApi.engineStatus(),
      ]);
      setStats(s); setJobs(j); setEngine(e);
    } catch (e) { err(e, "Failed to load call queue."); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    if (isEnrollmentTab) return;
    let active = true;
    (async () => { await Promise.resolve(); if (active) load(); })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, activeTab]);

  const runNow = async () => {
    if (!selCampaign) { toast.error("Select an active campaign."); return; }
    setRunning(true);
    try {
      const r = await aiAgentsApi.runCampaign(selCampaign);
      if (r.skipped) toast.info(`Nothing queued (${r.skipped.replace(/_/g, " ")}).`);
      else toast.success(`Queued ${r.created} · ${r.due} due · ${r.canceled} canceled · ${r.eligible} eligible estimate(s).`);
      load();
    } catch (e) { err(e, "Could not run campaign."); }
    finally { setRunning(false); }
  };

  const loadMemberships = async () => {
    if (!selCampaign) { toast.error("Select an active membership campaign."); return; }
    setRunning(true);
    try {
      const r = await aiAgentsApi.loadMembershipCampaign(selCampaign);
      if (r.skipped) toast.info(`Nothing loaded (${r.skipped.replace(/_/g, " ")}).`);
      else toast.success(`Loaded ${r.created} call(s) for ${r.eligible} customer(s) without an active membership.`);
      load();
    } catch (e) { err(e, "Could not load eligible customers."); }
    finally { setRunning(false); }
  };

  const cancel = async (id) => {
    try { await aiAgentsApi.cancelCallJob(id); toast.success("Call canceled."); load(); }
    catch (e) { err(e, "Could not cancel."); }
  };
  const snooze = async (id) => {
    try { await aiAgentsApi.snoozeCallJob(id); toast.success("Snoozed 24 hours — attempt not consumed."); load(); }
    catch (e) { err(e, "Could not snooze."); }
  };
  const remove = async (id) => {
    if (!window.confirm("Permanently remove this customer from the queue? They will not be re-added.")) return;
    try { await aiAgentsApi.removeCallJob(id); toast.success("Removed from queue."); load(); }
    catch (e) { err(e, "Could not remove."); }
  };

  return (
    <div data-testid="call-queue">
      {/* Tabs */}
      <div className="inline-flex rounded-lg bg-white border border-slate-200 p-1 mb-4">
        {allTabs.map((t) => (
          <button
            key={t.key} data-testid={`queue-tab-${t.key}`} onClick={() => setActiveTab(t.key)}
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[13.5px] font-medium transition-colors ${activeTab === t.key ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-slate-800"}`}
          >
            <t.Icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {isEnrollmentTab ? (
        <EnrollmentQueue bucket={activeTab} isAdmin={isAdmin} />
      ) : (
      <>
      {/* Controls */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 text-[13px] text-slate-600">
          {engine && (
            <span data-testid="engine-badge" className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1 rounded-full border ${engine.simulation ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
              {engine.simulation ? <FlaskConical className="h-3.5 w-3.5" /> : <PhoneCall className="h-3.5 w-3.5" />}
              {engine.simulation ? "Simulation mode" : "Live · ElevenLabs"}
            </span>
          )}
          <span>
            {activeTab === "followups"
              ? "Auto-populates with customers who have an unsold estimate. Sold or converted estimates drop out immediately."
              : "Doesn't auto-populate — click Load eligible customers to queue everyone without an active membership."}
          </span>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <select
              data-testid="queue-campaign-select"
              value={selCampaign} onChange={(e) => setSelCampaign(e.target.value)}
              className="h-9 px-3 rounded-lg border border-slate-300 text-[13.5px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
            >
              {tabCampaigns.length === 0 && <option value="">No active {tabDef.label.toLowerCase()} campaigns</option>}
              {tabCampaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {activeTab === "followups" ? (
              <Button data-testid="run-now-btn" onClick={runNow} disabled={running || !selCampaign} className="bg-indigo-600 hover:bg-indigo-700">
                {running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />} Run now
              </Button>
            ) : (
              <Button data-testid="load-memberships-btn" onClick={loadMemberships} disabled={running || !selCampaign} className="bg-indigo-600 hover:bg-indigo-700">
                {running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <UserRoundSearch className="h-4 w-4 mr-1.5" />} Load eligible customers
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2.5 mb-4">
        {[
          ["total", "Total", "text-slate-700"],
          ["due", "Due now", "text-amber-600"],
          ["queued", "Queued", "text-slate-500"],
          ["completed", "Completed", "text-emerald-600"],
          ["canceled", "Canceled", "text-slate-400"],
          ["failed", "Failed", "text-rose-600"],
        ].map(([k, label, cls]) => (
          <div key={k} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5" data-testid={`queue-stat-${k}`}>
            <div className={`text-[20px] font-bold ${cls}`}>{stats ? (stats[k] ?? 0) : "—"}</div>
            <div className="text-[11.5px] text-slate-500">{label}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        {["all", "due", "queued", "completed", "canceled"].map((f) => (
          <button key={f} data-testid={`queue-filter-${f}`} onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-[12.5px] font-medium capitalize transition-colors ${filter === f ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-500 hover:text-slate-800"}`}>
            {f}
          </button>
        ))}
      </div>

      {/* Jobs */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
      ) : jobs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-14 text-center" data-testid="queue-empty">
          {activeTab === "followups" ? <Phone className="h-9 w-9 text-slate-300 mx-auto mb-3" /> : <Users className="h-9 w-9 text-slate-300 mx-auto mb-3" />}
          <div className="font-semibold text-slate-700">No calls in this view</div>
          <div className="text-[13px] text-slate-500 mt-1">
            {activeTab === "followups"
              ? <>Activate a campaign and click <span className="font-medium">Run now</span> to queue follow-up calls for unsold estimates.</>
              : <>Activate a Membership sales campaign and click <span className="font-medium">Load eligible customers</span> to queue leads.</>}
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="queue-list">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-200 text-[11px] uppercase tracking-wider">
                  <th className="px-4 py-3 font-semibold">Customer</th>
                  <th className="px-4 py-3 font-semibold">{activeTab === "followups" ? "Estimate" : "Phone"}</th>
                  <th className="px-4 py-3 font-semibold text-center">Attempt</th>
                  <th className="px-4 py-3 font-semibold">Next call</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  {activeTab === "followups" && <th className="px-4 py-3 font-semibold">Outcome</th>}
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => {
                  const st = STATUS[j.status] || STATUS.queued;
                  const canSnoozeRemove = isAdmin && ["queued", "due"].includes(j.status);
                  const canSimulate = activeTab === "followups" && isAdmin && engine?.simulation && ["queued", "due", "in_progress"].includes(j.status);
                  return (
                    <tr key={j.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`queue-row-${j.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-800">{j.customerName}</td>
                      <td className="px-4 py-3 text-slate-600">{activeTab === "followups" ? j.estimateNumber : (j.customerPhone || "—")}</td>
                      <td className="px-4 py-3 text-center text-slate-600" data-testid={`queue-attempt-${j.id}`}>{j.attemptNumber}</td>
                      <td className="px-4 py-3 text-slate-600" data-testid={`queue-scheduled-${j.id}`}>
                        {activeTab === "followups" ? fmtDate(j.scheduledAt) : fmtDateTime(j.scheduledAt)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${st.cls}`}>
                          <st.Icon className="h-3 w-3" /> {st.label}
                        </span>
                      </td>
                      {activeTab === "followups" && (
                        <td className="px-4 py-3 text-[12.5px] text-slate-600">
                          {j.outcomeLabel || "—"}
                          {j.sentDoc && (
                            <div className="mt-0.5 inline-flex items-center gap-1 text-[10.5px] text-emerald-600" data-testid={`queue-sent-${j.id}`}>
                              <CheckCircle2 className="h-3 w-3" /> Sent {j.sentDoc.kind} {j.sentDoc.id}{(j.sentDoc.channels || []).length ? ` · ${j.sentDoc.channels.join("/")}` : ""}
                            </div>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {canSimulate && (
                            <button onClick={() => setSimJob(j)} data-testid={`queue-simulate-${j.id}`} className="inline-flex items-center gap-1 text-[12px] text-indigo-600 hover:text-indigo-700 px-2 py-1 rounded-md hover:bg-indigo-50 font-medium">
                              <FlaskConical className="h-3.5 w-3.5" /> Simulate
                            </button>
                          )}
                          {activeTab === "followups" && j.status === "completed" && (
                            <button onClick={() => setResultJob(j)} data-testid={`queue-result-${j.id}`} className="inline-flex items-center gap-1 text-[12px] text-slate-600 hover:text-slate-800 px-2 py-1 rounded-md hover:bg-slate-100">
                              <FileAudio className="h-3.5 w-3.5" /> Result
                            </button>
                          )}
                          {activeTab === "followups" && isAdmin && ["queued", "due", "in_progress", "completed"].includes(j.status) && (
                            <button onClick={() => setSendJob(j)} data-testid={`queue-send-${j.id}`} className="inline-flex items-center gap-1 text-[12px] text-emerald-600 hover:text-emerald-700 px-2 py-1 rounded-md hover:bg-emerald-50 font-medium">
                              <Send className="h-3.5 w-3.5" /> {j.sentDoc ? "Re-send" : "Send"}
                            </button>
                          )}
                          {activeTab === "followups" && (
                            <button onClick={() => setCtxJob(j)} data-testid={`queue-context-${j.id}`} className="inline-flex items-center gap-1 text-[12px] text-indigo-600 hover:text-indigo-700 px-2 py-1 rounded-md hover:bg-indigo-50">
                              <Eye className="h-3.5 w-3.5" /> Context
                            </button>
                          )}
                          {canSnoozeRemove && (
                            <>
                              <button onClick={() => snooze(j.id)} data-testid={`queue-snooze-${j.id}`} title="Push next call out 24h — doesn't use an attempt" className="inline-flex items-center gap-1 text-[12px] text-amber-600 hover:text-amber-700 px-2 py-1 rounded-md hover:bg-amber-50 font-medium">
                                <Moon className="h-3.5 w-3.5" /> Snooze
                              </button>
                              <button onClick={() => remove(j.id)} data-testid={`queue-remove-${j.id}`} title="Permanently remove from this campaign's queue" className="inline-flex items-center gap-1 text-[12px] text-red-500 hover:text-red-600 px-2 py-1 rounded-md hover:bg-red-50 font-medium">
                                <Trash2 className="h-3.5 w-3.5" /> Remove
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {ctxJob && <ContextDrawer job={ctxJob} onClose={() => setCtxJob(null)} />}
      {simJob && <SimulateModal job={simJob} onClose={() => setSimJob(null)} onDone={() => { setSimJob(null); load(); }} />}
      {resultJob && <ResultDrawer job={resultJob} onClose={() => setResultJob(null)} />}
      {sendJob && <SendDocModal job={sendJob} onClose={() => setSendJob(null)} onDone={() => { setSendJob(null); load(); }} />}
      </>
      )}
    </div>
  );
}

function EnrollmentQueue({ bucket, isAdmin }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setRows(await aiAgentsApi.listEnrollments(bucket)); }
    catch (e) { err(e, "Failed to load."); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    setSelected({});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket]);

  const remove = async (id) => {
    if (!window.confirm("Stop future automated calls for this estimate and move it to Inactive?")) return;
    try { await aiAgentsApi.removeEnrollment(id); toast.success("Removed from Call Queue."); load(); }
    catch (e) { err(e, "Could not remove."); }
  };

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const addBack = async () => {
    if (selectedIds.length === 0) return;
    setBusy(true);
    try {
      const r = await aiAgentsApi.reactivateEnrollments(selectedIds);
      const ok = r.results.filter((x) => x.ok).length;
      const fails = r.results.filter((x) => !x.ok);
      if (ok) toast.success(`Added ${ok} estimate(s) back to the Call Queue.`);
      fails.forEach((f) => toast.error(f.error));
      setSelected({});
      load();
    } catch (e) { err(e, "Could not add to queue."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`enrollment-queue-${bucket}`}>
      {bucket === "inactive" && isAdmin && (
        <div className="flex items-center justify-between mb-3">
          <div className="text-[13px] text-slate-500" data-testid="enrollment-selected-count">{selectedIds.length} selected</div>
          <Button data-testid="add-to-queue-btn" onClick={addBack} disabled={busy || selectedIds.length === 0} className="bg-indigo-600 hover:bg-indigo-700">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1.5" />} Add to Call Queue
          </Button>
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-14 text-center" data-testid={`enrollment-empty-${bucket}`}>
          {bucket === "active" ? <ListChecks className="h-9 w-9 text-slate-300 mx-auto mb-3" /> : <Archive className="h-9 w-9 text-slate-300 mx-auto mb-3" />}
          <div className="font-semibold text-slate-700">{bucket === "active" ? "No estimates currently active" : "Nothing inactive right now"}</div>
          <div className="text-[13px] text-slate-500 mt-1">
            {bucket === "active"
              ? "Estimates enrolled in a follow-up campaign will show up here."
              : "Removed or exhausted estimate enrollments will show up here."}
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`enrollment-list-${bucket}`}>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-200 text-[11px] uppercase tracking-wider">
                  {bucket === "inactive" && isAdmin && <th className="px-4 py-3 w-8"></th>}
                  <th className="px-4 py-3 font-semibold">Customer</th>
                  <th className="px-4 py-3 font-semibold">Estimate</th>
                  <th className="px-4 py-3 font-semibold text-right">Amount</th>
                  <th className="px-4 py-3 font-semibold">{bucket === "active" ? "Campaign" : "Previous campaign"}</th>
                  {bucket === "active" ? (
                    <>
                      <th className="px-4 py-3 font-semibold text-center">Attempt</th>
                      <th className="px-4 py-3 font-semibold">Last call</th>
                      <th className="px-4 py-3 font-semibold">Next call</th>
                      <th className="px-4 py-3 font-semibold">Last Result</th>
                    </>
                  ) : (
                    <>
                      <th className="px-4 py-3 font-semibold text-center">Completed attempts</th>
                      <th className="px-4 py-3 font-semibold">Last call</th>
                      <th className="px-4 py-3 font-semibold">Last Result</th>
                    </>
                  )}
                  <th className="px-4 py-3 font-semibold">{bucket === "active" ? "Status" : "Reason/status"}</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const st = ENR_STATUS[r.status] || ENR_STATUS.waiting;
                  return (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`enrollment-row-${r.id}`}>
                      {bucket === "inactive" && isAdmin && (
                        <td className="px-4 py-3">
                          <input
                            type="checkbox" data-testid={`enrollment-checkbox-${r.id}`} checked={!!selected[r.id]}
                            onChange={(e) => setSelected((s) => ({ ...s, [r.id]: e.target.checked }))}
                            className="h-4 w-4 accent-indigo-600"
                          />
                        </td>
                      )}
                      <td className="px-4 py-3 font-medium text-slate-800">{r.customerName || "—"}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/estimates?id=${r.estimateId}`)}
                          data-testid={`enrollment-estimate-link-${r.id}`}
                          className="text-indigo-600 hover:text-indigo-700 hover:underline font-medium"
                          title="Open this estimate (send history, payment link, line items)"
                        >
                          {r.estimateNumber}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-right">${Number(r.estimateAmount || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-slate-600">{r.campaignName || "—"}</td>
                      {bucket === "active" ? (
                        <>
                          <td className="px-4 py-3 text-center text-slate-600" data-testid={`enrollment-attempt-${r.id}`}>
                            Attempt {r.currentAttempt || 1} of {r.maxAttempts}
                          </td>
                          <td className="px-4 py-3 text-slate-600">{fmtDate(r.lastCallAt)}</td>
                          <td className="px-4 py-3 text-slate-600">{fmtDate(r.nextCallAt)}</td>
                          <td className="px-4 py-3 text-slate-600" data-testid={`enrollment-last-result-${r.id}`}>{r.lastOutcomeLabel || "—"}</td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-3 text-center text-slate-600">{r.attemptsCompleted || 0} of {r.maxAttempts}</td>
                          <td className="px-4 py-3 text-slate-600">{fmtDate(r.lastCallAt)}</td>
                          <td className="px-4 py-3 text-slate-600" data-testid={`enrollment-last-result-${r.id}`}>{r.lastOutcomeLabel || "—"}</td>
                        </>
                      )}
                      <td className="px-4 py-3" data-testid={`enrollment-status-${r.id}`}>
                        <span className="inline-flex items-center gap-1.5 flex-wrap">
                          {bucket === "active" && (
                            <span className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full border bg-blue-50 text-blue-700 border-blue-200 font-semibold" data-testid={`enrollment-estimate-status-${r.id}`}>
                              Unsold
                            </span>
                          )}
                          <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${st.cls}`}>
                            <st.Icon className="h-3 w-3" /> {bucket === "active" ? st.label : (r.completionReasonLabel || REASON_LABEL[r.completionReason] || st.label)}
                          </span>
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {bucket === "active" && isAdmin && (
                          <button
                            onClick={() => remove(r.id)} data-testid={`enrollment-remove-${r.id}`}
                            title="Remove from Call Queue"
                            className="inline-flex items-center gap-1 text-[12px] text-red-500 hover:text-red-600 px-2 py-1 rounded-md hover:bg-red-50 font-medium"
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Remove from Call Queue
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SendDocModal({ job, onClose, onDone }) {
  const [channel, setChannel] = useState("smart");
  const [busy, setBusy] = useState(false);
  const CHANNELS = [
    { key: "smart", label: "Email + Text (smart)", Icon: Send },
    { key: "email", label: "Email only", Icon: Mail },
    { key: "sms", label: "Text only", Icon: MessageSquare },
  ];
  const run = async () => {
    setBusy(true);
    try {
      const r = await aiAgentsApi.sendDoc(job.id, channel);
      if (r.warning) toast.warning(r.warning);
      else toast.success(`Sent ${r.kind} ${r.docId} via ${(r.channels || []).join(" & ") || "—"}.`);
      onDone();
    } catch (e) { err(e, "Could not send the document."); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" data-testid="send-doc-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Send className="h-4.5 w-4.5 text-emerald-600" /> Send estimate / invoice</h3>
          <button onClick={onClose} data-testid="send-doc-close" className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-[12.5px] text-slate-500 mb-4">{job.customerName} · {job.estimateNumber}. If the estimate is accepted it's converted to an invoice and sent with a pay link; otherwise the estimate is sent with an accept link.</p>
        <label className="block text-[12.5px] font-medium text-slate-700 mb-1.5">Channel</label>
        <div className="space-y-2 mb-4">
          {CHANNELS.map((c) => (
            <button key={c.key} onClick={() => setChannel(c.key)} data-testid={`send-channel-${c.key}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg border-2 text-left text-[13.5px] transition-colors ${channel === c.key ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}>
              <c.Icon className="h-4 w-4" /> {c.label}
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={run} disabled={busy} data-testid="send-doc-confirm" className="bg-emerald-600 hover:bg-emerald-700">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Send now
          </Button>
        </div>
      </div>
    </div>
  );
}

function SimulateModal({ job, onClose, onDone }) {
  const [outcome, setOutcome] = useState("estimate_accepted");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await aiAgentsApi.simulateCall(job.id, outcome);
      toast.success("Call simulated — CRM updated.");
      onDone();
    } catch (e) { err(e, "Could not simulate call."); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" data-testid="simulate-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold text-slate-800 flex items-center gap-2"><FlaskConical className="h-4.5 w-4.5 text-indigo-600" /> Simulate call</h3>
          <button onClick={onClose} data-testid="simulate-close" className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-[12.5px] text-slate-500 mb-4">{job.customerName} · {job.estimateNumber} · attempt {job.attemptNumber}. Choose how the call ends — the CRM write-back runs exactly as it will with live calls.</p>
        <label className="block text-[12.5px] font-medium text-slate-700 mb-1">Outcome</label>
        <select data-testid="simulate-outcome-select" value={outcome} onChange={(e) => setOutcome(e.target.value)}
          className="w-full h-9 px-3 rounded-lg border border-slate-300 text-[13.5px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 mb-4">
          {OUTCOME_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={run} disabled={busy} data-testid="simulate-run-btn" className="bg-indigo-600 hover:bg-indigo-700">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <PhoneCall className="h-4 w-4 mr-1.5" />} Run simulated call
          </Button>
        </div>
      </div>
    </div>
  );
}

function ResultDrawer({ job, onClose }) {
  const mins = Math.floor((job.durationSec || 0) / 60);
  const secs = Math.round((job.durationSec || 0) % 60);
  return (
    <div className="fixed inset-0 z-[100] flex" data-testid="result-drawer">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3.5 flex items-center justify-between z-10">
          <div>
            <h3 className="font-semibold text-slate-800">Call result</h3>
            <p className="text-[12px] text-slate-500">{job.customerName} · {job.estimateNumber} · attempt {job.attemptNumber}</p>
          </div>
          <button onClick={onClose} data-testid="result-close" className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1 text-[12px] bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-2.5 py-1">{job.outcomeLabel || job.outcome}</span>
            <span className="inline-flex items-center gap-1 text-[12px] bg-slate-100 text-slate-600 rounded-full px-2.5 py-1"><Clock className="h-3.5 w-3.5" /> {mins}m {secs}s</span>
            <span className={`inline-flex items-center gap-1 text-[12px] rounded-full px-2.5 py-1 border ${job.connected ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{job.connected ? "Connected" : "Not connected"}</span>
          </div>
          {job.recordingUrl && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Recording</div>
              <a href={job.recordingUrl} target="_blank" rel="noreferrer" className="text-[13px] text-indigo-600 hover:underline inline-flex items-center gap-1.5" data-testid="result-recording">
                <FileAudio className="h-4 w-4" /> {job.recordingUrl}
              </a>
            </div>
          )}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Transcript</div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[13px] text-slate-700 whitespace-pre-wrap" data-testid="result-transcript">
              {job.transcript || "No transcript."}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContextDrawer({ job, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showScripts, setShowScripts] = useState(false);
  useEffect(() => {
    let active = true;
    (async () => {
      try { const r = await aiAgentsApi.callJobContext(job.id); if (active) setData(r.context); }
      catch (e) { err(e, "Could not load context."); if (active) onClose(); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [job.id]);

  const Row = CtxRow;

  return (
    <div className="fixed inset-0 z-[100] flex" data-testid="context-drawer">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3.5 flex items-center justify-between z-10">
          <div>
            <h3 className="font-semibold text-slate-800">Call context</h3>
            <p className="text-[12px] text-slate-500">What the agent receives before dialing</p>
          </div>
          <button onClick={onClose} data-testid="context-close" className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>
        {loading || !data ? (
          <div className="flex items-center justify-center flex-1"><Loader2 className="h-6 w-6 text-indigo-500 animate-spin" /></div>
        ) : (
          <div className="px-5 py-4 space-y-4">
            <Row icon={User} label="Customer">
              {data.customer.name} · {data.customer.phone}
              {data.customer.membership && <span className="ml-2 text-[11px] bg-violet-50 text-violet-700 border border-violet-200 rounded-full px-1.5 py-0.5">{data.customer.membership}</span>}
            </Row>
            <Row icon={FileText} label="Estimate">
              {data.estimate.number} · ${Number(data.estimate.total).toLocaleString()}
              <div className="text-[12px] text-slate-500 mt-0.5">{(data.estimate.services || []).join(", ")}</div>
            </Row>
            <Row icon={Wrench} label="Technician">{data.technician.name || "Unassigned"}</Row>
            <Row icon={Building2} label="Company">{data.company.name}{data.company.phone ? ` · ${data.company.phone}` : ""}</Row>
            <Row icon={Sparkles} label="Agent">{data.agent.name} · voice {data.agent.voiceName || data.agent.voiceId || "—"}{data.agent.offerText ? ` · ${data.agent.offerText}` : ""}</Row>

            {/* Knowledge */}
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" /> Knowledge the agent can cite ({(data.knowledge || []).length})</div>
              {(data.knowledge || []).length === 0 ? (
                <div className="text-[12.5px] text-slate-400">No knowledge matched — add items in the Knowledge Base tab.</div>
              ) : (
                <div className="space-y-1.5">
                  {data.knowledge.map((k, i) => (
                    <div key={i} className="rounded-lg border border-slate-200 p-2.5" data-testid={`context-kb-${i}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-[12px] font-semibold text-slate-700">{k.title}</span>
                        <span className="text-[10.5px] text-slate-400">{Math.round((k.score || 0) * 100)}%</span>
                      </div>
                      <div className="text-[12px] text-slate-500 line-clamp-2 mt-0.5">{k.text}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Scripts (collapsible) */}
            <div className="border-t border-slate-100 pt-3">
              <button onClick={() => setShowScripts((v) => !v)} data-testid="context-scripts-toggle" className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-700">
                {showScripts ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />} Conversation scripts
              </button>
              {showScripts && (
                <div className="mt-2 space-y-2.5">
                  {[["Opening", data.agent.scripts.opening], ["Offer", data.agent.scripts.offer], ["Objection handling", data.agent.scripts.objectionHandling], ["Transfer rules", data.agent.scripts.transferRules], ["Escalation", data.agent.scripts.escalationRules], ["Closing", data.agent.scripts.closing]].map(([label, val]) => (
                    <div key={label}>
                      <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
                      <div className="text-[12.5px] text-slate-600 whitespace-pre-wrap">{val || <span className="text-slate-300">—</span>}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
